import { useRef, useState } from "react";

import { Alert, Button, Label, TextInput } from "@/ui";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/contexts/auth-context";
import GoogleSignInButton from "@/features/auth/google-sign-in-button";

export default function Login() {
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const { login, signInWithGoogle, isLoggedIn } = useAuth();
  const [error, setError] = useState<String>();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    try {
      setError("");
      setLoading(true);
      await login(emailRef.current!.value, passwordRef.current!.value);
      navigate(searchParams.get("redirect") || "/workouts");
    } catch (err) {
      setError("Failed to log in");
    }

    setLoading(false);
  }

  async function handleContinueWithGoogleClicked(
    e: React.MouseEvent<HTMLButtonElement>
  ) {
    e.preventDefault();
    try {
      setError("");
      setLoading(true);
      await signInWithGoogle();
      navigate(searchParams.get("redirect") || "/workouts");
    } catch (err) {
      setError("Failed to sign in");
    }
    setLoading(false);
  }

  if (isLoggedIn) return <Navigate to="/workouts" />;

  return (
    <>
      <section className="w-full min-h-full">
        <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto">
          <a
            href="/"
            className="flex items-center mb-6 text-2xl text-ink-primary font-extrabold tracking-tight"
          >
            Lift Notes
          </a>
          {searchParams.get("redirect") !== null && (
            <span className="mb-3">Log in to proceed.</span>
          )}
          <div className="w-full bg-surface-card rounded-card border border-line-subtle md:mt-0 sm:max-w-md xl:p-0">
            <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
              <h1 className="text-xl font-bold leading-tight tracking-tight text-ink-primary md:text-2xl">
                Sign in
              </h1>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {error && (
                  <Alert color="failure">
                    <span>{error}</span>
                  </Alert>
                )}
                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="email1">Email</Label>
                  </div>
                  <TextInput
                    id="email1"
                    type="email"
                    ref={emailRef}
                    placeholder="name@swole.net"
                    required={true}
                    autoComplete="on"
                  />
                </div>
                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="password1">Password</Label>
                  </div>
                  <TextInput
                    id="password1"
                    type="password"
                    ref={passwordRef}
                    required={true}
                    autoComplete="on"
                  />
                </div>
                <div className="py-2.5">
                  <Button
                    variant="gradient"
                    disabled={loading}
                    type="submit"
                    fullWidth
                  >
                    Sign in
                  </Button>{" "}
                </div>
                <div className="flex items-center my-3 before:flex-1 before:border-t before:border-line-strong before:mt-0.5 after:flex-1 after:border-t after:border-line-strong after:mt-0.5">
                  <p className="text-center font-semibold mx-4 mb-0 text-ink-secondary">
                    OR
                  </p>
                </div>
                <GoogleSignInButton
                  disabled={loading}
                  onClick={handleContinueWithGoogleClicked}
                />
                <div className="w-100 text-center mt-3 text-sm font-light text-ink-muted">
                  <Link
                    to="/forgot_password"
                    className="font-medium text-ink-primary hover:underline"
                  >
                    Forgot Password?
                  </Link>
                </div>
                <p className="text-sm font-light text-ink-muted">
                  Need an account?{" "}
                  <Link
                    to={`/signup?${searchParams.toString()}`}
                    className="font-medium text-ink-primary hover:underline"
                  >
                    Sign Up
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
